<template>
  <div class="form-overlay" @click.self="$emit('cancel')">
    <div class="form-container">
      <form class="lease-mapping-form" @submit.prevent="onSubmit">
        <div class="form-header">
          <h2>Mapping toevoegen</h2>
          <button type="button" class="close-btn" @click="$emit('cancel')">&times;</button>
        </div>

        <div class="form-body">
          <div class="form-group">
            <label for="dealerId">Dealer ID *</label>
            <input
              id="dealerId"
              v-model.trim="form.DealerId"
              type="text"
              class="form-control"
              data-qa="lease-mapping-form-dealer-id"
              @blur="v$.form.DealerId.$touch()"
            />
            <small v-if="v$.form.DealerId.$error" class="field-error">Dit veld is verplicht.</small>
          </div>

          <div class="form-group">
            <label for="debtorId">Debtor ID *</label>
            <input
              id="debtorId"
              v-model.trim="form.DebtorId"
              type="text"
              class="form-control"
              data-qa="lease-mapping-form-debtor-id"
              @blur="v$.form.DebtorId.$touch()"
            />
            <small v-if="v$.form.DebtorId.$error" class="field-error">Dit veld is verplicht.</small>
          </div>

          <div class="form-group">
            <label for="crmNumber">CRM Nummer *</label>
            <input
              id="crmNumber"
              v-model.trim="form.CrmNumber"
              type="text"
              class="form-control"
              data-qa="lease-mapping-form-crm-number"
              @blur="v$.form.CrmNumber.$touch()"
            />
            <small v-if="v$.form.CrmNumber.$error" class="field-error"
              >Dit veld is verplicht.</small
            >
          </div>

          <div class="form-group">
            <label for="customerId">Klant ID</label>
            <input
              id="customerId"
              v-model.trim="form.CustomerId"
              type="text"
              class="form-control"
              data-qa="lease-mapping-form-customer-id"
            />
            <small class="form-help">Optioneel</small>
          </div>
        </div>

        <div class="form-footer">
          <app-button
            button-class="inverted--bordered"
            data-qa="lease-mapping-form-cancel"
            type="button"
            @click="$emit('cancel')"
            >Annuleren</app-button
          >
          <app-button button-type="submit" data-qa="lease-mapping-form-submit" :disabled="loading">
            <span v-if="loading">Opslaan...</span>
            <span v-else>Mapping toevoegen</span>
          </app-button>
        </div>

        <div v-if="error" class="error-message" data-qa="lease-mapping-form-error">
          {{ error }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import leaseMappingService from '@/services/leaseMappingService'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default {
  name: 'LeaseMappingForm',
  components: { AppButton },
  emits: ['cancel', 'saved'],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      form: {
        DealerId: '',
        DebtorId: '',
        CrmNumber: '',
        CustomerId: '',
      },
      loading: false,
      error: null,
    }
  },
  validations() {
    return {
      form: {
        DealerId: { required },
        DebtorId: { required },
        CrmNumber: { required },
      },
    }
  },
  methods: {
    async onSubmit() {
      this.error = null
      const valid = await this.v$.$validate()
      if (!valid) {
        return
      }

      this.loading = true
      try {
        const payload = {
          DealerId: this.form.DealerId,
          DebtorId: this.form.DebtorId,
          CrmNumber: this.form.CrmNumber,
          CustomerId: this.form.CustomerId || null,
        }
        const response = await leaseMappingService.createLeaseMapping(payload)
        this.$emit('saved', response.data)
      } catch (e) {
        if (e.response?.status === 409) {
          this.error = 'Er bestaat al een mapping voor deze Dealer ID en Debtor ID.'
        } else {
          this.error =
            e.response?.data?.message ||
            e.message ||
            'Het opslaan van de mapping is mislukt. Probeer het opnieuw.'
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.form-container {
  background: $white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
}

.lease-mapping-form {
  display: flex;
  flex-direction: column;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid $gray-lighter;
}

.form-header h2 {
  margin: 0;
  color: $gray-darker;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: $gray-dark;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}
.close-btn:hover {
  background-color: $gray-lightest;
}

.form-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: $gray-darker;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid $gray-lighter;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: $color-primary;
}

.form-help {
  display: block;
  margin-top: 0.25rem;
  color: $gray-dark;
  font-size: 0.875rem;
}

.field-error {
  display: block;
  margin-top: 0.25rem;
  color: $color-danger;
  font-size: 0.875rem;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid $gray-lighter;
  background-color: $gray-lightest;
}

.error-message {
  margin: 1rem 1.5rem;
  padding: 1rem;
  background-color: rgba($color-danger, 0.1);
  color: $color-danger;
  border: 1px solid rgba($color-danger, 0.3);
  border-radius: 4px;
}
</style>
